import BlockTitle from '@/src/components/Modules-revamp/BlockTitle';
import ModuleWidth from '@/src/components/Modules-revamp/ModuleWidthSelect';
import { Field } from '@/src/components/RcForm';
import {
  ResponsiveContainer,
  ResponsiveField,
  formAnimationList,
  formButtonList,
} from '@/src/strategies/util';
import { get } from 'lodash';
import React from 'react';
import Animation from 'src/components/Modules-revamp/Animation';
import Wrapper from 'src/components/Modules-revamp/BlockWrapper';
import Images from 'src/components/Modules-revamp/Images';
import ResizingOptions from 'src/components/Modules-revamp/ResponsiveResizingOptionSelect';
import { ContentModuleGroupPreloadingContext } from 'src/constrains/DraggableModules/PreloadComponent/SubscriptionPreloading';
import posterRenderTemplate from 'src/strategies/render/poster';
import { EnumModuleType } from '../../../../pages/SalesChannelContentEditor/common';
import moduleStyleRender from '../../ModuleStyle';

import Gap from '@/src/components/Modules-revamp/Gap';
import PosterType from '@/src/components/Modules-revamp/PosterType';
import {
  checkResponsivePoster,
  validatePosterCarouselSetting,
} from 'src/constrains/SalesChannelContent-Revamp/ModuleList/validator';
import { useImagePoster } from '../../hooks/useImagePoster';

type ResponsiveFieldProps = {
  field: ResponsiveField | ResponsiveField[];
  disabled?: boolean;
  options?: Array<{ label: string; value: any }>;
  hasModal?: boolean;
  modalFunc?: any;
};
const posterComponent = (
  data: ResponsiveFieldProps,
  breakpoint: any,
  extra: any,
) => {
  const {
    onModalConfirm,
    onAdd,
    onDelete,
    onSort,
    animations,
    hoverAnimations,
    buttons,
    form,
    onTypeChange,
    disabled,
  } = extra || {};
  const draggableId = get(form.getFieldsValue(true), 'draggableId');
  switch (data.field) {
    case ResponsiveField.PosterType: {
      return (
        <PosterType {...data} onTypeChange={onTypeChange} disabled={disabled} />
      );
    }
    case ResponsiveField.Gap: {
      return <Gap {...data} disabled={disabled} />;
    }
    case ResponsiveField.ModuleWidth: {
      return (
        <ModuleWidth
          disabled={data?.disabled || disabled}
          options={data?.options || []}
        />
      );
    }
    case ResponsiveField.ResizingOption: {
      return (
        <ResizingOptions
          disabled={data?.disabled || disabled}
          options={data?.options || []}
        />
      );
    }
    case ResponsiveField.Animation: {
      return (
        <Animation
          disabled={data?.disabled || disabled}
          animationOptions={formAnimationList(animations || [])}
          hoverEffectOptions={formAnimationList(hoverAnimations || [])}
        />
      );
    }
    case ResponsiveField.Images: {
      const { checkImage, checkImageLink, checkName } =
        validatePosterCarouselSetting();
      const { checkPoster } = checkResponsivePoster();
      return (
        <Images
          validators={{
            name: checkName,
            image: checkImage,
            link: checkImageLink,
          }}
          allValidator={checkPoster}
          moduleType={EnumModuleType.ResponsivePoster}
          animationOptions={formAnimationList(animations || [])}
          hoverEffectOptions={formAnimationList(hoverAnimations || [])}
          buttons={formButtonList(buttons || [])}
          draggableId={draggableId}
          breakpoint={breakpoint}
          disabled={data?.disabled}
          hasModal={data?.hasModal}
          modalFunc={data?.modalFunc}
          onModalConfirm={onModalConfirm}
          onAdd={onAdd}
          onDelete={onDelete}
          onSort={onSort}
          translateEnabled={disabled}
        />
      );
    }
    default:
      return;
  }
};
const posterRender = (form: any, breakpoint: any, extra: any) => {
  const values = form.getFieldsValue(true);
  const { handleOnSectionReset, handleOnSectionCopy, disabled } = extra;
  const list = posterRenderTemplate(values, breakpoint);
  const renderComponents = (
    {
      type,
      field,
    }: {
      title: string;
      type?: ResponsiveContainer;
      field: Array<any>;
    },
    breakpoint: any,
  ) => {
    if (type === ResponsiveContainer.ModuleStyle) {
      return field.map((f) => moduleStyleRender(f, disabled));
    } else {
      return field.map((f) => posterComponent(f, breakpoint, extra));
    }
  };
  return (
    <>
      {list.map((item, index) => {
        const component = renderComponents(item, breakpoint);
        return (
          <Wrapper
            key={`${item.title}_${index}`}
            id={`${item?.id}`}
            breakpoint={breakpoint}>
            <BlockTitle
              id={item?.id}
              title={item?.title}
              breakpoint={breakpoint}
              onReset={() => handleOnSectionReset(item?.fields, breakpoint)}
              onCopy={() => handleOnSectionCopy(item?.fields, breakpoint)}
              headerErrorDependencies={get(item, 'headerErrorDependencies')}
              validator={get(item, 'validator')}
              disabled={disabled}
            />

            {component}
          </Wrapper>
        );
      })}
    </>
  );
};

export default ({ form, breakpoint, ...extra }: any) => {
  const {
    handleOnModalConfirm,
    handleOnAdd,
    handleOnDelete,
    handleOnSort,
    handleOnTypeChange,
  } = useImagePoster({ form });
  const { animations, hoverAnimations, buttons } = React.useContext(
    ContentModuleGroupPreloadingContext,
  );
  return (
    <Field shouldUpdate>
      {() =>
        posterRender(form, breakpoint, {
          onModalConfirm: handleOnModalConfirm,
          onAdd: handleOnAdd,
          onDelete: handleOnDelete,
          onSort: handleOnSort,
          onTypeChange: handleOnTypeChange,
          animations,
          buttons,
          hoverAnimations,
          form,
          ...extra,
        })
      }
    </Field>
  );
};
